import {
  CreateClientRequest,
  CreateSessionRequest,
  type Client,
  type Session,
  type PricingPlanItem,
} from '@diana/contracts';
import { AdminApiError } from '../../lib/api-client.js';

export type RequestDraft = {
  mode: 'existing' | 'new';
  selectedClient: Client | null;
  name: string;
  email: string;
  phone: string;
  instagram: string;
  title: string;
  isGiftCard: boolean;
  notes: string;
  internalNotes: string;
  status: 'NewRequest' | 'InDiscussion' | 'Scheduled';
  package: PricingPlanItem | null;
  currency: string;
  dateTime: string;
  scheduledEndAt: string | null;
  location: string;
  price: string;
};
export type RequestErrors = Partial<Record<keyof RequestDraft, string>>;
type RequestIds = { clientId: string; sessionId: string };
type PreparedRequest = {
  errors: RequestErrors;
  client: CreateClientRequest | null;
  session: CreateSessionRequest | null;
};
export type RequestCreationActions = {
  createClient(body: CreateClientRequest): Promise<Client>;
  createSession(body: CreateSessionRequest): Promise<Session>;
};
export type CreationSnapshot = {
  pending: boolean;
  completed: boolean;
  createdClient: Client | null;
  locked: boolean;
  clientLocked: boolean;
  errors: RequestErrors;
  failure: { stage: 'client' | 'session'; message: string } | null;
};
export function emptyRequestDraft(): RequestDraft {
  return {
    mode: 'existing',
    selectedClient: null,
    name: '',
    email: '',
    phone: '',
    instagram: '',
    title: '',
    isGiftCard: false,
    notes: '',
    internalNotes: '',
    status: 'NewRequest',
    package: null,
    currency: 'EUR',
    dateTime: '',
    scheduledEndAt: null,
    location: '',
    price: '',
  };
}
const optional = (value: string) => value.trim() || null;
const fieldNames: Record<string, keyof RequestDraft> = {
  scheduledStartAt: 'dateTime',
  scheduledEndAt: 'dateTime',
  priceAmountMinor: 'price',
  clients: 'selectedClient',
};
function addIssues(
  errors: RequestErrors,
  issues: readonly {
    path: readonly PropertyKey[];
    message: string;
    code: string;
    maximum?: number | bigint;
  }[],
) {
  for (const issue of issues) {
    const raw = String(issue.path[0]);
    const key = fieldNames[raw] ?? (raw as keyof RequestDraft);
    const labels: Record<string, string> = {
      name: 'Name',
      email: 'Email',
      phone: 'Phone',
      instagram: 'Instagram',
      title: 'Title',
      location: 'Location',
      notes: 'Message and notes',
      internalNotes: 'Notes',
    };
    const label = labels[key];
    const message =
      key === 'name' && issue.code === 'too_small'
        ? 'Enter a client name.'
        : key === 'email' && issue.code === 'invalid_format'
          ? 'Enter a valid email address.'
          : label && issue.code === 'too_big' && issue.maximum !== undefined
            ? `${label} must be ${issue.maximum} characters or fewer.`
            : issue.message;
    errors[key] ??= message;
  }
}
export function buildRequest(
  draft: RequestDraft,
  ids: RequestIds,
  savedClient: Client | null = null,
): PreparedRequest {
  const errors: RequestErrors = {};
  let client: CreateClientRequest | null = null;
  const selected = savedClient ?? (draft.mode === 'existing' ? draft.selectedClient : null);
  if (!savedClient && draft.mode === 'new') {
    const parsed = CreateClientRequest.safeParse({
      id: ids.clientId,
      name: draft.name.trim(),
      email: optional(draft.email),
      phone: optional(draft.phone),
      instagram: optional(draft.instagram),
      notes: null,
    });
    if (parsed.success) client = parsed.data;
    else addIssues(errors, parsed.error.issues);
  } else if (!selected || selected.archivedAt)
    errors.selectedClient = 'Select an active client or add a new client.';
  let scheduledStartAt: string | null = null;
  if (draft.dateTime) {
    const date = new Date(draft.dateTime);
    const parts = draft.dateTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
    if (
      !parts ||
      !Number.isFinite(date.getTime()) ||
      Number(parts[1]) < 1000 ||
      [
        date.getFullYear(),
        date.getMonth() + 1,
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
      ].some((part, index) => part !== Number(parts[index + 1]))
    ) {
      errors.dateTime = 'Enter a valid local date and time.';
    } else scheduledStartAt = date.toISOString();
  }
  let priceAmountMinor: number | null = null;
  if (draft.status === 'Scheduled' && !scheduledStartAt)
    errors.dateTime ??= 'Choose a date and start time for a scheduled shoot.';
  const price = draft.price.trim();
  if (price) {
    const match = price.match(/^(\d+)(?:[.,](\d{1,2}))?$/);
    if (match) priceAmountMinor = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0'));
    if (!match || !Number.isSafeInteger(priceAmountMinor) || priceAmountMinor! > 2_147_483_647)
      errors.price = 'Enter a price from 0 to 21474836.47 with at most two decimal places.';
  }
  const name = selected?.name ?? client?.name ?? draft.name.trim();
  const body = CreateSessionRequest.safeParse({
    id: ids.sessionId,
    title: optional(draft.title) ?? `Photography request — ${name}`.slice(0, 200),
    status: draft.status,
    isGiftCard: draft.isGiftCard,
    sessionType: null,
    scheduledStartAt,
    scheduledEndAt: scheduledStartAt ? draft.scheduledEndAt : null,
    location: optional(draft.location),
    priceAmountMinor,
    expensesAmountMinor: 0,
    currency: draft.currency,
    notes: optional(draft.notes),
    ...(draft.internalNotes.trim() ? { internalNotes: draft.internalNotes.trim() } : {}),
    requestedPackageKey: draft.package?.key ?? null,
    requestedPackageTitle: draft.package?.payload.title ?? null,
    requestedPackagePriceLabel: draft.package?.payload.priceLabel ?? null,
    sourcePath: null,
    clients: [{ clientId: selected?.id ?? ids.clientId, position: 0, isPrimary: true }],
    checklist: [],
  });
  if (!body.success) addIssues(errors, body.error.issues);
  return { errors, client, session: body.success ? body.data : null };
}

/** Keeps an attempt's IDs/body in memory, including when the outcome is unknown. */
export class CreateRequestCoordinator {
  snapshot: CreationSnapshot = {
    pending: false,
    completed: false,
    createdClient: null,
    locked: false,
    clientLocked: false,
    errors: {},
    failure: null,
  };
  private captured: PreparedRequest | null = null;
  private ambiguous = { client: false, session: false };
  constructor(
    private actions: RequestCreationActions,
    private ids: RequestIds = { clientId: crypto.randomUUID(), sessionId: crypto.randomUUID() },
  ) {}
  async run(draft: RequestDraft): Promise<Session | null> {
    if (this.snapshot.pending || this.snapshot.completed) return null;
    if (!this.snapshot.locked)
      this.captured = buildRequest(draft, this.ids, this.snapshot.createdClient);
    const attempt = this.captured!;
    if (Object.keys(attempt.errors).length || !attempt.session) {
      this.snapshot = { ...this.snapshot, errors: attempt.errors, failure: null };
      return null;
    }
    this.snapshot = { ...this.snapshot, pending: true, errors: {}, failure: null };
    let stage: 'client' | 'session' =
      attempt.client && !this.snapshot.createdClient ? 'client' : 'session';
    try {
      if (stage === 'client' && attempt.client) {
        const createdClient = await this.actions.createClient(structuredClone(attempt.client));
        this.snapshot = { ...this.snapshot, createdClient, clientLocked: true };
      }
      stage = 'session';
      const session = await this.actions.createSession(structuredClone(attempt.session));
      this.snapshot = { ...this.snapshot, completed: true, locked: true, clientLocked: true };
      return session;
    } catch (error) {
      const definite =
        error instanceof AdminApiError &&
        ((error.status === 422 && error.code === 'VALIDATION_FAILED') ||
          (error.status === 401 && error.code === 'UNAUTHORIZED') ||
          (error.status === 403 && ['FORBIDDEN', 'CSRF_REJECTED'].includes(error.code)));
      const conflict = error instanceof AdminApiError && error.code === 'CREATE_CONFLICT';
      if (!definite) this.ambiguous[stage] = true;
      const locked = this.ambiguous[stage];
      const errors: RequestErrors = {};
      if (error instanceof AdminApiError && definite && !locked) {
        for (const field of error.fields ?? []) {
          const key =
            fieldNames[field.path.split('.')[0]!] ??
            (field.path.split('.')[0] as keyof RequestDraft);
          const duplicate =
            stage === 'client' &&
            ['email', 'phone', 'instagram'].includes(key) &&
            /already|duplicate|exists/i.test(field.message);
          errors[key] = duplicate
            ? `${field.message}. Choose the existing client instead.`
            : field.message;
        }
      }
      this.snapshot = {
        ...this.snapshot,
        locked,
        clientLocked: locked || Boolean(this.snapshot.createdClient),
        errors,
        failure: {
          stage,
          message: conflict
            ? 'This ID already belongs to a different record. Check Shoots and Clients before continuing; the same ID is retained.'
            : locked
              ? `The ${stage === 'client' ? 'client' : 'request'} may have been saved. Retry creation to check safely with the exact same details.`
              : error instanceof AdminApiError && [401, 403].includes(error.status)
                ? 'Your session or access needs attention. Sign in again, then retry.'
                : 'Check the highlighted details and create the request again.',
        },
      };
      return null;
    } finally {
      this.snapshot = { ...this.snapshot, pending: false };
    }
  }
}
